import { useState, useRef, useEffect, useCallback } from "react";
import CustomSelectProductStyles from "./CustomSelectProductStyles.module.css";

type OptionType = {
  value: string;
  label: string;
};

type CustomSelectProductProps = {
  options: OptionType[];
  onSelect: (option: OptionType) => void;
  placeholder: string;
  value?: OptionType | null;
  id?: string;
};

const CustomSelectProduct: React.FC<CustomSelectProductProps> = ({
  options,
  onSelect,
  placeholder,
  value,
  id,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(
    value || null
  );
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

  const listboxId = id ? `${id}-listbox` : "custom-select-listbox";

  const handleSelectOption = (option: OptionType) => {
    setSelectedOption(option);
    onSelect(option);
    setIsOpen(false);
    setActiveIndex(-1);
    buttonRef.current?.focus();
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setActiveIndex(selectedOption ? options.findIndex(o => o.value === selectedOption.value) : 0);
    }
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    setSelectedOption(value || null);
  }, [value]);

  // Scroll active option into view
  useEffect(() => {
    if (isOpen && activeIndex >= 0 && optionRefs.current[activeIndex]) {
      optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (isOpen && activeIndex >= 0) {
          handleSelectOption(options[activeIndex]);
        } else {
          toggleDropdown();
        }
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setActiveIndex(0);
        } else {
          setActiveIndex((prev) => Math.min(prev + 1, options.length - 1));
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (isOpen) {
          setActiveIndex((prev) => Math.max(prev - 1, 0));
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        buttonRef.current?.focus();
        break;
      case "Tab":
        setIsOpen(false);
        setActiveIndex(-1);
        break;
      case "Home":
        if (isOpen) {
          e.preventDefault();
          setActiveIndex(0);
        }
        break;
      case "End":
        if (isOpen) {
          e.preventDefault();
          setActiveIndex(options.length - 1);
        }
        break;
    }
  };

  return (
    <div className={CustomSelectProductStyles.customSelect} ref={dropdownRef}>
      <button
        type="button"
        ref={buttonRef}
        className={CustomSelectProductStyles.selectDisplay}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={isOpen && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
      >
        <span>{selectedOption ? selectedOption.label : placeholder}</span>
        <i
          className={`fa-solid ${
            isOpen
              ? `fa-chevron-up ${CustomSelectProductStyles.icon}`
              : `fa-chevron-down ${CustomSelectProductStyles.icon}`
          }`}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <ul 
          ref={listRef}
          id={listboxId}
          className={CustomSelectProductStyles.optionList}
          role="listbox"
          aria-label={placeholder}
          tabIndex={-1}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              ref={(el) => (optionRefs.current[index] = el)}
              id={`${listboxId}-option-${index}`}
              className={`${CustomSelectProductStyles.optionItem} ${
                activeIndex === index ? CustomSelectProductStyles.optionItemActive : ""
              } ${selectedOption?.value === option.value ? CustomSelectProductStyles.optionItemSelected : ""}`}
              role="option"
              aria-selected={selectedOption?.value === option.value}
              onClick={() => handleSelectOption(option)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CustomSelectProduct;
